import { useCallback, useEffect, useRef, useState } from "react";
import { pharmacyInventoryApi } from "@/core/inventory/inventory-api";
import { IscApiError } from "@/core/inventory/isc-client";
import type { StockProduct } from "@/core/pharmacy/types";

export function isApiOfflineError(err: unknown): boolean {
  return err instanceof IscApiError && err.status === 0;
}

interface UseBarcodeScanOptions {
  minLength?: number;
  debounceMs?: number;
  onFound?: (product: StockProduct, barcode: string) => void;
  onNotFound?: (barcode: string) => void;
}

export function useBarcodeScan(options: UseBarcodeScanOptions = {}) {
  const { minLength = 4, debounceMs = 400, onFound, onNotFound } = options;

  const scanRef = useRef<HTMLInputElement>(null);
  const scanTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastScanRef = useRef("");

  const [scanValue, setScanValue] = useState("");
  const [scanLoading, setScanLoading] = useState(false);
  const [scanStatus, setScanStatus] = useState<string | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  const [apiOffline, setApiOffline] = useState(false);
  const [lastBarcode, setLastBarcode] = useState<string | null>(null);
  const [product, setProduct] = useState<StockProduct | null>(null);

  const focusScan = useCallback(() => {
    scanRef.current?.focus();
  }, []);

  const resetScanInput = useCallback(() => {
    lastScanRef.current = "";
    setScanValue("");
    setScanStatus(null);
  }, []);

  const runScanLookup = useCallback(
    async (code: string) => {
      const trimmed = code.trim();
      if (!trimmed || scanLoading) return;
      if (lastScanRef.current === trimmed) return;

      lastScanRef.current = trimmed;
      setScanLoading(true);
      setScanStatus(null);
      setScanError(null);
      setLastBarcode(trimmed);

      try {
        const found = await pharmacyInventoryApi.findByBarcode(trimmed);
        setScanValue("");

        if (!found) {
          lastScanRef.current = "";
          setProduct(null);
          setScanStatus(null);
          onNotFound?.(trimmed);
          return;
        }

        setProduct(found);
        setScanStatus(`Article trouvé : ${found.label}`);
        onFound?.(found, trimmed);
      } catch (err) {
        lastScanRef.current = "";
        setProduct(null);
        setApiOffline(isApiOfflineError(err));
        setScanError(
          isApiOfflineError(err)
            ? "API stock inaccessible."
            : err instanceof Error
              ? err.message
              : "Recherche impossible.",
        );
      } finally {
        setScanLoading(false);
        scanRef.current?.focus();
      }
    },
    [onFound, onNotFound, scanLoading],
  );

  useEffect(() => {
    const trimmed = scanValue.trim();
    if (trimmed.length < minLength || scanLoading) return;

    if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
    scanTimerRef.current = setTimeout(() => {
      void runScanLookup(trimmed);
    }, debounceMs);

    return () => {
      if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
    };
  }, [scanValue, scanLoading, runScanLookup, minLength, debounceMs]);

  const handleScanKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
    void runScanLookup(scanValue);
  };

  const handleScanChange = (value: string) => {
    lastScanRef.current = "";
    setScanValue(value);
    setScanStatus(null);
    setScanError(null);
  };

  const lookupBarcode = useCallback(
    (barcode: string) => {
      setScanValue(barcode);
      void runScanLookup(barcode);
    },
    [runScanLookup],
  );

  return {
    scanRef,
    scanValue,
    scanLoading,
    scanStatus,
    scanError,
    apiOffline,
    lastBarcode,
    product,
    setProduct,
    focusScan,
    resetScanInput,
    handleScanChange,
    handleScanKeyDown,
    lookupBarcode,
    runScanLookup,
  };
}
